import { defineComponent as _defineComponent } from 'vue'
import AppButton from "@/components/AppButton.vue";
import AppField from "@/components/AppField.vue";
import { computed, ref, watch } from "vue";
import { ImageTransform } from "../types";
import { clamp } from "../lib/clamp";
import { useDraggable } from "../lib/draggable";
import { useCanvases } from "../lib/canvases";
import { useDownload } from "../lib/download";


export default /*#__PURE__*/_defineComponent({
  __name: 'ResizeUpload',
  props: {
    image: null,
    transform: null
  },
  emits: ["update-transform", "upload", "reset"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: "update-transform", transform: Partial<ImageTransform>): unknown;
  (e: "upload", image: HTMLImageElement | undefined): unknown;
  (e: "reset"): unknown;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  image?: HTMLImageElement;
  transform: ImageTransform;
}

const { downloadImage } = useCanvases();
const { startDownload, downloading } = useDownload();





const width = ref(1000);
const height = ref(1000);

const resizeElement = ref<HTMLElement>();
const imageElement = ref<HTMLImageElement>();
const cropElement = ref<HTMLElement>();
const imageLoaded = ref(false);

const download = async () => {
  if (props.image) {
    await downloadImage(props.image, {
      ...props.transform,
      cropWidth: cropWidth.value,
      cropHeight: cropHeight.value
    });
  }
};

const onDragStart = () => {
  if (imageElement.value && cropElement.value) {
    imgWidth = imageElement.value.clientWidth;
    imgHeight = imageElement.value.clientHeight;
    frameWidth = cropElement.value.clientWidth;
    frameHeight = cropElement.value.clientHeight;
    initialLeft = cropElement.value.offsetLeft;
    initialTop = cropElement.value.offsetTop;
  }
};

const onDragMove = ({ x, y }: { x: number; y: number }): void => {
  const maxX = clamp(-initialLeft, x, imgWidth - frameWidth - initialLeft);
  const maxY = clamp(-initialTop, y, imgHeight - frameHeight - initialTop);

  const cropX = clamp(0, (initialLeft + maxX + frameWidth / 2) / imgWidth, 1);
  const cropY = clamp(0, (initialTop + maxY + frameHeight / 2) / imgHeight, 1);

  emits("update-transform", {
    cropX,
    cropY
  });
};

const { dragStart } = useDraggable({
  onDragStart,
  onDragMove
});

let imgWidth: number;
let imgHeight: number;
let frameWidth: number;
let frameHeight: number;
let initialLeft: number;
let initialTop: number;

const imageURL = computed(() => {
  return props.image?.src;
});

const uploading = ref(false);

watch(
  () => props.image,
  (newImage) => {
    if (!newImage && imageLoaded) {
      imageLoaded.value = false;
    }
  }
);

const crop = computed(() => {
  if (imageElement.value && imageLoaded.value && props.image) {
    return getCrop(imageElement.value);
  } else {
    return {};
  }
});

const cropWidth = computed(() => clamp(1, width.value, 10_000));
const cropHeight = computed(() => clamp(1, height.value, 10_000));

const getCrop = (image: HTMLImageElement) => {
  const cropY = props.transform.cropY;
  const cropX = props.transform.cropX;

  // The image's natural dimensions
  const imgWidth = image.naturalWidth;
  const imgHeight = image.naturalHeight;

  // The actual height of the image
  const clientWidth = image.clientWidth;
  const clientHeight = image.clientHeight;

  const scale = Math.max(cropWidth.value / imgWidth, cropHeight.value / imgHeight);

  const w = (cropWidth.value / scale / imgWidth) * clientWidth;
  const h = (cropHeight.value / scale / imgHeight) * clientHeight;
  const l = clamp(0, cropX * clientWidth - w / 2, clientWidth - w);
  const t = clamp(0, cropY * clientHeight - h / 2, clientHeight - h);

  return {
    width: w + "px",
    height: h + "px",
    left: l + "px",
    top: t + "px",
    right: "auto",
    bottom: "auto"
  };
};

const onImageLoaded = (): void => {
  imageLoaded.value = true;
};

const resizePreviewStyles = computed(() => {
  if (!resizeElement.value || !props.image) {
    return undefined;
  }

  const frameRatio = resizeElement.value.clientHeight / resizeElement.value.clientWidth;
  const imageRatio = props.image.naturalHeight / props.image.naturalWidth;

  if (frameRatio < imageRatio) {
    return {
      width:
        (((props.image.naturalWidth / props.image.naturalHeight) * resizeElement.value.clientHeight) /
          resizeElement.value.clientWidth) *
          100 +
        "%"
    };
  } else {
    return {
      height:
        (((props.image.naturalHeight / props.image.naturalWidth) * resizeElement.value.clientWidth) /
          resizeElement.value.clientHeight) *
          100 +
        "%"
    };
  }
});

const loadImage = async (file: File): Promise<HTMLImageElement | undefined> => {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
        URL.revokeObjectURL(image.src);
        reject();
      }

      resolve(image);
      // URL.revokeObjectURL(image.src);
    };

    image.onerror = () => {
      reject();
    };

    image.src = URL.createObjectURL(file);
  });
};

const onFileChange = async ({ target }: Event): Promise<void> => {
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  uploading.value = true;
  const file = target.files && target.files[0];

  if (file) {
    try {
      const image = await loadImage(file);

      if (image) {
        emits("upload", image);
      }
    } finally {
      uploading.value = false;
    }
  }
};

return { __sfc: true,downloadImage, startDownload, downloading, emits, props, width, height, resizeElement, imageElement, cropElement, imageLoaded, download, onDragStart, onDragMove, dragStart, imgWidth, imgHeight, frameWidth, frameHeight, initialLeft, initialTop, imageURL, uploading, crop, cropWidth, cropHeight, getCrop, onImageLoaded, resizePreviewStyles, loadImage, onFileChange, AppButton, AppField }
}

})